import type { BatchQueryEntry } from '@moment/horizon-data-access/platform'

import { getEntityContext } from '@moment/sorm-client-data-access/entity-context'
import chunk from 'lodash/chunk'

import { SECURITY_ENTITY, SECURITY_ID_FIELD } from './security-wire-fields'

// Live inventory matches every list's values against the bond's ISIN, CUSIP
// and ticker, whatever field the list was built on.
const MEMBERSHIP_KEY_FIELDS = [SECURITY_ID_FIELD, 'cusip', 'bond_ticker']

// SORM caps a query at 64 computed columns; each list costs one column of
// three IN_FILTER_LIST calls.
export const FILTER_LISTS_PER_MEMBERSHIP_ENTRY = 20

const MEMBERSHIP_ISINS_PER_ENTRY = 1_000
const MEMBERSHIP_ENTRY_PREFIX = '__filter_lists'

/** The org's lists, at most `FILTER_LISTS_PER_MEMBERSHIP_ENTRY` per entry, in list order. */
export function filterListMembershipChunks(
  listIds: ReadonlyArray<string>,
): string[][] {
  return chunk([...new Set(listIds)], FILTER_LISTS_PER_MEMBERSHIP_ENTRY)
}

function stringLiteral(value: string): string {
  return `'${value.replaceAll("'", "''")}'`
}

/** Whether a Security row's ISIN, CUSIP or ticker is on the list. */
export function filterListMembershipExpr(listId: string): string {
  return MEMBERSHIP_KEY_FIELDS.map(
    (field) =>
      `IN_FILTER_LIST(${SECURITY_ENTITY}.${field}, ${stringLiteral(listId)})`,
  ).join(' OR ')
}

const membershipAlias = (index: number) => `in_filter_list_${index}`

export interface FilterListMembershipPlan {
  /** Batch entries to send alongside the row read; none for no lists or ISINs. */
  entries: BatchQueryEntry[]
  /** The list IDs holding each ISIN, in list order, from the entries' rows. */
  read: (rowsById: ReadonlyMap<string, unknown[]>) => Map<string, string[]>
}

/**
 * Filter-list membership of `isins` as Security batch entries of their own,
 * one computed column per list, so they never share the computed-column cap
 * with objective or tax columns.
 */
export function planFilterListMembership(
  isins: ReadonlyArray<string>,
  listIds: ReadonlyArray<string>,
): FilterListMembershipPlan {
  const lists = [...new Set(listIds)]
  const listChunks = filterListMembershipChunks(lists)
  const isinChunks = chunk([...new Set(isins)], MEMBERSHIP_ISINS_PER_ENTRY)
  const entries: BatchQueryEntry[] = []
  const entryLists = new Map<string, ReadonlyArray<string>>()
  listChunks.forEach((chunkLists, listIndex) => {
    isinChunks.forEach((chunkIsins, isinIndex) => {
      const id = `${MEMBERSHIP_ENTRY_PREFIX}:${listIndex}:${isinIndex}`
      entryLists.set(id, chunkLists)
      entries.push({
        id,
        query: {
          root_entity: SECURITY_ENTITY,
          context: getEntityContext(SECURITY_ENTITY),
          fields: [
            {
              entity: SECURITY_ENTITY,
              field: SECURITY_ID_FIELD,
              alias: SECURITY_ID_FIELD,
            },
            ...chunkLists.map((listId, index) => ({
              alias: membershipAlias(index),
              expr: filterListMembershipExpr(listId),
            })),
          ],
          filters: {
            [`${SECURITY_ENTITY}.${SECURITY_ID_FIELD}`]: { in: chunkIsins },
          },
          limit: chunkIsins.length,
        },
      })
    })
  })

  const read = (rowsById: ReadonlyMap<string, unknown[]>) => {
    const held = new Map<string, Set<string>>()
    for (const [id, chunkLists] of entryLists) {
      for (const row of (rowsById.get(id) ?? []) as Record<string, unknown>[]) {
        const isin = row[SECURITY_ID_FIELD]
        if (typeof isin !== 'string') continue
        const isinLists = held.get(isin) ?? new Set<string>()
        chunkLists.forEach((listId, index) => {
          if (row[membershipAlias(index)] === true) isinLists.add(listId)
        })
        held.set(isin, isinLists)
      }
    }
    return new Map(
      [...held].map(([isin, isinLists]) => [
        isin,
        lists.filter((listId) => isinLists.has(listId)),
      ]),
    )
  }

  return { entries, read }
}
